import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * BackupsPage — view, create, restore, and delete CMS snapshots.
 * Snapshots cover every page_content, SEO, settings, scripts, blog,
 * showcase, and media-metadata row. Media file bytes already live in
 * GridFS and are never touched here.
 *
 * Auto-backups run daily. Manual snapshots can be taken before any
 * risky operation (e.g. before a big content overhaul, before a push
 * to production).
 */
export default function BackupsPage() {
  const [backups, setBackups] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState("");
  const [error, setError] = useState("");
  const [confirmRestoreId, setConfirmRestoreId] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");

  const refresh = async () => {
    try {
      const list = await api.backupsList();
      setBackups(list);
    } catch (e) {
      setError(e.message || "Could not load backups.");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const createNow = async () => {
    setBusy(true);
    setError("");
    try {
      await api.backupsCreate();
      await refresh();
    } catch (e) {
      setError(e.message || "Backup failed.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (id) => {
    setRestoring(id);
    setError("");
    try {
      await api.backupsRestore(id);
      await refresh();
      setConfirmRestoreId("");
      // Reload public-facing pages might need a full refresh to pick up content
      alert(
        "Restore complete. A safety snapshot of the previous state was taken automatically before the restore (look for the 'pre-restore' label)."
      );
    } catch (e) {
      setError(e.message || "Restore failed.");
    } finally {
      setRestoring("");
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      await api.backupsDelete(id);
      setConfirmDeleteId("");
      await refresh();
    } catch (e) {
      setError(e.message || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-backups-page">
      <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
        Safety net
      </div>
      <h1 className="mt-3 font-display text-4xl font-black tracking-[-0.025em] leading-[1.05]">
        CMS Backups
      </h1>
      <p className="mt-5 max-w-2xl text-[0.95rem] text-neutral-400 leading-[1.65]">
        Daily auto-snapshots of every CMS collection. Media files are
        already durable (GridFS) and aren&rsquo;t included here. The
        most recent 7 auto-snapshots are kept; manual snapshots are
        kept forever. Restoring a backup automatically takes a fresh
        snapshot of the current state first so you can always undo.
      </p>

      <div className="mt-8 flex items-center gap-3">
        <button
          type="button"
          onClick={createNow}
          disabled={busy}
          data-testid="admin-backups-create-btn"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase transition-colors ${
            busy
              ? "bg-white/[0.06] text-neutral-500 cursor-not-allowed"
              : "bg-[#F4D64E] text-neutral-950 hover:bg-[#FFE36B]"
          }`}
        >
          {busy ? "Snapshotting…" : "+ Take Snapshot Now"}
        </button>
        <button
          type="button"
          onClick={refresh}
          className="text-[0.7rem] font-bold tracking-[0.18em] uppercase text-neutral-400 hover:text-white transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div
          data-testid="admin-backups-error"
          className="mt-6 max-w-3xl rounded-md border border-red-500/30 bg-red-500/[0.06] px-5 py-4 text-[0.85rem] text-red-300"
        >
          {error}
        </div>
      )}

      <div className="mt-10">
        <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-4">
          Available snapshots ({(backups || []).length})
        </div>
        {backups === null && (
          <div className="text-[0.8rem] text-neutral-500">Loading…</div>
        )}
        {backups && backups.length === 0 && (
          <div className="text-[0.8rem] text-neutral-500">
            No backups yet. Take one now.
          </div>
        )}
        <ul className="flex flex-col gap-3">
          {(backups || []).map((b) => {
            const labelColor =
              b.label === "auto"
                ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
                : b.label === "manual"
                ? "text-[#F4D64E] bg-[#F4D64E]/10 border-[#F4D64E]/30"
                : "text-amber-300 bg-amber-500/10 border-amber-500/30";
            const isConfirmRestore = confirmRestoreId === b.id;
            const isConfirmDelete = confirmDeleteId === b.id;
            return (
              <li
                key={b.id}
                data-testid="admin-backup-row"
                className="bg-[#0F141A] border border-white/[0.06] rounded-xl p-5"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[0.6rem] font-bold tracking-[0.2em] uppercase border ${labelColor}`}
                  >
                    {b.label}
                  </span>
                  <span className="text-[0.85rem] text-neutral-300 font-mono">
                    {new Date(b.created_at).toLocaleString()}
                  </span>
                  <span className="text-[0.7rem] text-neutral-500 font-mono">
                    id: {b.id}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                  {Object.entries(b.counts || {}).map(([k, v]) => (
                    <div
                      key={k}
                      className="rounded-md bg-white/[0.04] border border-white/[0.06] px-3 py-2"
                    >
                      <div className="text-[0.55rem] font-bold tracking-[0.18em] uppercase text-neutral-500 truncate">
                        {k.replace(/_/g, " ")}
                      </div>
                      <div className="mt-0.5 font-display text-base font-black tracking-[-0.01em] tabular-nums">
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  {!isConfirmRestore && !isConfirmDelete && (
                    <>
                      <button
                        type="button"
                        onClick={() => setConfirmRestoreId(b.id)}
                        data-testid="admin-backup-restore-btn"
                        className="px-4 py-2 rounded-md text-[0.65rem] font-bold tracking-[0.18em] uppercase bg-white/[0.06] hover:bg-[#F4D64E] hover:text-neutral-950 text-neutral-200 transition-colors"
                      >
                        Restore →
                      </button>
                      {b.label !== "auto" || (backups.filter((x) => x.label === "auto").length > 1) ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(b.id)}
                          className="px-4 py-2 rounded-md text-[0.65rem] font-bold tracking-[0.18em] uppercase bg-white/[0.04] hover:bg-red-500/30 hover:text-red-200 text-neutral-500 transition-colors"
                        >
                          Delete
                        </button>
                      ) : null}
                    </>
                  )}
                  {isConfirmRestore && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[0.78rem] text-amber-300">
                        Restoring will overwrite all current CMS data. A pre-restore snapshot will be saved automatically.
                      </span>
                      <button
                        type="button"
                        onClick={() => restore(b.id)}
                        disabled={restoring === b.id}
                        data-testid="admin-backup-restore-confirm"
                        className="px-4 py-2 rounded-md text-[0.65rem] font-bold tracking-[0.18em] uppercase bg-[#F4D64E] text-neutral-950 hover:bg-[#FFE36B] transition-colors"
                      >
                        {restoring === b.id ? "Restoring…" : "Confirm Restore"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRestoreId("")}
                        className="text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-500 hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {isConfirmDelete && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[0.78rem] text-red-300">
                        Delete this backup permanently?
                      </span>
                      <button
                        type="button"
                        onClick={() => remove(b.id)}
                        className="px-4 py-2 rounded-md text-[0.65rem] font-bold tracking-[0.18em] uppercase bg-red-500/40 text-red-100 hover:bg-red-500/60 transition-colors"
                      >
                        Confirm Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId("")}
                        className="text-[0.65rem] font-bold tracking-[0.18em] uppercase text-neutral-500 hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
